// src/store/slices/checkoutSlice.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as checkoutApi from '../api/checkout';

// Async thunks
export const fetchAddresses = createAsyncThunk(
  'checkout/fetchAddresses',
  async (_, { rejectWithValue }) => {
    try {
      const response = await checkoutApi.getAddresses();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch addresses');
    }
  }
);

export const addAddress = createAsyncThunk(
  'checkout/addAddress',
  async (addressData, { rejectWithValue }) => {
    try {
      const response = await checkoutApi.addAddress(addressData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to add address');
    }
  }
);

export const updateAddress = createAsyncThunk(
  'checkout/updateAddress',
  async ({ addressId, addressData }, { rejectWithValue }) => {
    try {
      const response = await checkoutApi.updateAddress(addressId, addressData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update address');
    }
  }
);

export const deleteAddress = createAsyncThunk(
  'checkout/deleteAddress',
  async (addressId, { rejectWithValue }) => {
    try {
      const response = await checkoutApi.deleteAddress(addressId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete address');
    }
  }
);

export const setDefaultAddress = createAsyncThunk(
  'checkout/setDefaultAddress',
  async (addressId, { rejectWithValue }) => {
    try {
      const response = await checkoutApi.setDefaultAddress(addressId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to set default address');
    }
  }
);

export const fetchOrderSummary = createAsyncThunk(
  'checkout/fetchOrderSummary',
  async (_, { rejectWithValue }) => {
    try {
      const response = await checkoutApi.getOrderSummary();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch order summary');
    }
  }
);

export const createOrder = createAsyncThunk(
  'checkout/createOrder',
  async (orderData, { rejectWithValue }) => {
    try {
      const response = await checkoutApi.createOrder(orderData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create order');
    }
  }
);

// Initial state
const initialState = {
  addresses: [],
  orderSummary: null,
  selectedAddress: null,
  billingAddress: 'same',
  paymentMethod: 'COD',
  orderNotes: '',
  createdOrder: null,
  loading: {
    addresses: false,
    addAddress: false,
    updateAddress: false,
    deleteAddress: false,
    orderSummary: false,
    createOrder: false
  },
  error: {
    addresses: null,
    addAddress: null,
    updateAddress: null,
    deleteAddress: null,
    orderSummary: null,
    createOrder: null
  }
};

// Slice
const checkoutSlice = createSlice({
  name: 'checkout',
  initialState,
  reducers: {
    selectAddress: (state, action) => {
      state.selectedAddress = action.payload;
    },
    setBillingAddress: (state, action) => {
      state.billingAddress = action.payload;
    },
    setPaymentMethod: (state, action) => {
      state.paymentMethod = action.payload;
    },
    setOrderNotes: (state, action) => {
      state.orderNotes = action.payload;
    },
    resetCheckout: (state) => {
      state.orderSummary = null;
      state.selectedAddress = null;
      state.billingAddress = 'same';
      state.paymentMethod = 'COD';
      state.orderNotes = '';
      state.createdOrder = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch addresses
    builder
      .addCase(fetchAddresses.pending, (state) => {
        state.loading.addresses = true;
        state.error.addresses = null;
      })
      .addCase(fetchAddresses.fulfilled, (state, action) => {
        state.addresses = action.payload.data.addresses;
        state.loading.addresses = false;
        
        // Set default address if available
        if (state.addresses.length > 0 && !state.selectedAddress) {
          const defaultAddress = state.addresses.find(addr => addr.isDefault);
          if (defaultAddress) {
            state.selectedAddress = defaultAddress.addressId;
          } else {
            state.selectedAddress = state.addresses[0].addressId;
          }
        }
      })
      .addCase(fetchAddresses.rejected, (state, action) => {
        state.loading.addresses = false;
        state.error.addresses = action.payload?.message || 'Failed to fetch addresses';
      });
      
    // Add address
    builder
      .addCase(addAddress.pending, (state) => {
        state.loading.addAddress = true;
        state.error.addAddress = null;
      })
      .addCase(addAddress.fulfilled, (state, action) => {
        state.addresses.push(action.payload.data.address);
        state.selectedAddress = action.payload.data.address.addressId;
        state.loading.addAddress = false;
      })
      .addCase(addAddress.rejected, (state, action) => {
        state.loading.addAddress = false;
        state.error.addAddress = action.payload?.message || 'Failed to add address';
      });
      
    // Update address
    builder
      .addCase(updateAddress.pending, (state) => {
        state.loading.updateAddress = true;
        state.error.updateAddress = null;
      })
      .addCase(updateAddress.fulfilled, (state, action) => {
        const updatedAddress = action.payload.data.address;
        const index = state.addresses.findIndex(addr => addr.addressId === updatedAddress.addressId);
        
        if (index !== -1) {
          state.addresses[index] = updatedAddress;
        }
        
        state.loading.updateAddress = false;
      })
      .addCase(updateAddress.rejected, (state, action) => {
        state.loading.updateAddress = false;
        state.error.updateAddress = action.payload?.message || 'Failed to update address';
      });
      
    // Delete address
    builder
      .addCase(deleteAddress.pending, (state) => {
        state.loading.deleteAddress = true;
        state.error.deleteAddress = null;
      })
      .addCase(deleteAddress.fulfilled, (state, action) => {
        const addressId = action.meta.arg;
        state.addresses = state.addresses.filter(addr => addr.addressId !== addressId);
        
        // Update selected address if it was deleted
        if (state.selectedAddress === addressId) {
          const defaultAddress = state.addresses.find(addr => addr.isDefault);
          state.selectedAddress = defaultAddress ? defaultAddress.addressId : 
                                 (state.addresses.length > 0 ? state.addresses[0].addressId : null);
        }
        
        state.loading.deleteAddress = false;
      })
      .addCase(deleteAddress.rejected, (state, action) => {
        state.loading.deleteAddress = false;
        state.error.deleteAddress = action.payload?.message || 'Failed to delete address';
      });
      
    // Fetch order summary
    builder
      .addCase(fetchOrderSummary.pending, (state) => {
        state.loading.orderSummary = true;
        state.error.orderSummary = null;
      })
      .addCase(fetchOrderSummary.fulfilled, (state, action) => {
        state.orderSummary = action.payload.data.summary;
        state.loading.orderSummary = false;
      })
      .addCase(fetchOrderSummary.rejected, (state, action) => {
        state.loading.orderSummary = false;
        state.error.orderSummary = action.payload?.message || 'Failed to fetch order summary';
      });
      
    // Create order
    builder
      .addCase(createOrder.pending, (state) => {
        state.loading.createOrder = true;
        state.error.createOrder = null;
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.createdOrder = action.payload.data;
        state.loading.createOrder = false;
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.loading.createOrder = false;
        state.error.createOrder = action.payload?.message || 'Failed to create order';
      });
  }
});

export const { 
  selectAddress, 
  setBillingAddress, 
  setPaymentMethod, 
  setOrderNotes,
  resetCheckout
} = checkoutSlice.actions;

export default checkoutSlice.reducer;